import { ActionSettings, BasicSelect, ICONS, Modal, ToolbarButton } from '@fl/cmsch-fe-library';
import React, { FC, Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOurTranslation } from 'app/translations';
import { Ant } from 'common/ant';
import { ConnectedTable } from 'utils/tables';
import { breedersColumns } from './columns';
import { useManagementBreedersFormSetup } from '../../hooks/use-management-breeders-form-setup';
import { adminAction } from '../../model/action';
import { simpleAdminSelector } from '../../model/selector';
import { ManagementBreedersTableType } from '../../types/management-breeders';
import { SubjectManagementFacilityForm } from '../subject-management-facility-form';

// eslint-disable-next-line max-lines-per-function
const ManagementBreedersTableBase: FC = _ => {
  const {
    t,
    tCommon
  } = useOurTranslation('admin');
  const dispatch = useDispatch();
  const {
    farmsSettings,
    userProfileAndRoles,
    roleOptions,
    userProfileOptions,
    visibleModal,
    actionMode,
    breederId,
    breedersOptions,
    emptyTableTooltip,
    breederName,
    breederDetail,
    hasAccessToAllFarms,
    rowMapper,
    onTableFetch,
    removeUsersFromRole,
    removeConfirmTitle,
    removeRolesForUser,
    searchBreeders,
    changeValue,
    handleCloseModal,
    handleOpenCreateModalWithoutLimitations,
    handleColumnOpenCreateModal,
    handleRowOpenCreateModal,
    handleRoleSearch,
    handleSubmitModal,
    handleUserSearch,
    validate,
    validateDebounced
  } = useManagementBreedersFormSetup();
  const extraColumns = useSelector(simpleAdminSelector.managementBreedersExtraColumns);
  const leftHeaderCustomElement = useMemo(() => <BasicSelect value={breederId} options={breedersOptions} onFieldChange={changeValue} onSearch={searchBreeders} ignoreSearchMinLength fullWidth testId="breeder-select" />, [breederId, breedersOptions, changeValue, searchBreeders]);
  useEffect(() => {
    dispatch(adminAction.getBreedersOptions(''));
  }, [dispatch]);
  useEffect(() => {
    if (breederId.isFull) dispatch(adminAction.getBreedersFarms(breederId.orCrash('No breeder selected')));
  }, [breederId, dispatch]);
  const addAccessToUserDisabledTitle = useCallback((_id: number, user: ManagementBreedersTableType) => t('cannotCreateRecord', {
    user: user.username
  }), [t]);
  const isAddAccessToUserDisabled = useCallback((_id: number, user: ManagementBreedersTableType) => !user.isSubjectMember, []);
  const actionSettings: ActionSettings<ManagementBreedersTableType> = useMemo(() => ({
    confirmRowRemove: true,
    extraActions: [{
      id: 'delete',
      role: 'delete',
      callback: removeRolesForUser,
      confirmClick: true,
      confirmTitle: removeConfirmTitle
    }, {
      id: 'create',
      title: t('addAccessToUser'),
      disabledTitle: addAccessToUserDisabledTitle,
      disabled: isAddAccessToUserDisabled,
      role: 'create',
      callback: handleRowOpenCreateModal
    }]
  }), [t, handleRowOpenCreateModal, removeConfirmTitle, removeRolesForUser, isAddAccessToUserDisabled, addAccessToUserDisabledTitle]);
  const headerButtons: Array<ToolbarButton> = useMemo(() => [{
    title: t('access'),
    role: 'create',
    disabled: breederId.isEmpty,
    disabledTitle: t('needSelectBreeder'),
    onClick: handleOpenCreateModalWithoutLimitations
  }], [t, handleOpenCreateModalWithoutLimitations, breederId]);
  const computedColumns = useMemo(() => breederId.isFull ? breedersColumns(extraColumns, t, tCommon, removeUsersFromRole, handleColumnOpenCreateModal) : [], [breederId.isFull, extraColumns, handleColumnOpenCreateModal, removeUsersFromRole, t, tCommon]);
  return <Fragment>
            <ConnectedTable tableId="managementBreeders" columns={computedColumns} headerButtons={headerButtons} onFetchTableData={onTableFetch} rowDataToTableRowMapper={rowMapper} rowDataPageSelector={simpleAdminSelector.managementBreedersPage} withoutVerticalScrollbar actionSettings={actionSettings} leftHeaderCustomElement={leftHeaderCustomElement} paginationPosition="none" emptyTableMessages={{
      noColumnsVisible: emptyTableTooltip
    }} emptyTableBodyContent={ICONS.chooseOption} lazyLoadingLimit={20} showAlsoIfEmpty data-sentry-element="ConnectedTable" data-sentry-source-file="table.tsx" />

            {breederId.isFull && <Modal title={t(actionMode === 'create' ? 'addAccessToBreeder' : 'updateAccessToBreeder', {
      breeder: breederName
    })} visible={visibleModal && (actionMode === 'edit' ? breederDetail.isFull : true)} onCancel={handleCloseModal} footer={null}>
                <Ant.Row>
                    <Ant.Col xs={24}>
                        <SubjectManagementFacilityForm isEdit={actionMode === 'edit'} dataForEdit={breederDetail} hasAccessToAllFarms={hasAccessToAllFarms} onCancel={handleCloseModal} onSubmit={handleSubmitModal} userProfileOptions={userProfileOptions} userProfileTitle={t('loginName')} subjectFacilityFormType="BREEDER" onRoleSearch={handleRoleSearch} roleOptions={roleOptions} userProfileAndRole={userProfileAndRoles} onUserSearch={handleUserSearch} onSyncValidate={validateDebounced} farmsSettings={farmsSettings} validate={validate} subjectId={breederId.orCrash('breeder not found')} />
                    </Ant.Col>
                </Ant.Row>
            </Modal>}
        </Fragment>;
};

/** Breeder management table. */
export const ManagementBreedersTable = memo(ManagementBreedersTableBase);